/**
 * TodoVoiceButton - Mic button for voice input in the todo quick add bar.
 * Uses the shared voice recording hook and calls onTranscribed with the result.
 */

import { useCallback, useEffect } from 'react';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { ActivityIndicator, StyleSheet, TouchableOpacity } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated';

import { useVoiceRecording } from '@/hooks/use-voice-recording';

type TodoVoiceButtonProps = {
  accentColor: string;
  onTranscribed: (text: string) => void;
  onError?: (message: string) => void;
};

const BUTTON_SIZE = 26;

export function TodoVoiceButton({
  accentColor,
  onTranscribed,
  onError,
}: TodoVoiceButtonProps) {
  const {
    recordingState,
    transcribedText,
    error,
    startRecording,
    stopRecording,
    clearTranscription,
  } = useVoiceRecording();

  const pulseOpacity = useSharedValue(1);

  // Deliver transcription result to parent
  useEffect(() => {
    if (transcribedText) {
      onTranscribed(transcribedText);
      clearTranscription();
    }
  }, [transcribedText, onTranscribed, clearTranscription]);

  // Surface errors to parent
  useEffect(() => {
    if (error) {
      onError?.(error);
      clearTranscription();
    }
  }, [error, onError, clearTranscription]);

  const startPulse = useCallback(() => {
    pulseOpacity.value = withRepeat(
      withSequence(
        withTiming(0.4, { duration: 600 }),
        withTiming(1, { duration: 600 }),
      ),
      -1,
    );
  }, [pulseOpacity]);

  const stopPulse = useCallback(() => {
    pulseOpacity.value = withTiming(1, { duration: 200 });
  }, [pulseOpacity]);

  const handlePress = useCallback(async () => {
    if (recordingState === 'transcribing') return;

    if (recordingState === 'idle') {
      await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
      startPulse();
      await startRecording();
    } else {
      await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
      stopPulse();
      await stopRecording();
    }
  }, [recordingState, startRecording, stopRecording, startPulse, stopPulse]);

  const pulseStyle = useAnimatedStyle(() => ({
    opacity: recordingState === 'recording' ? pulseOpacity.value : 1,
  }));

  const isRecording = recordingState === 'recording';
  const isTranscribing = recordingState === 'transcribing';

  return (
    <Animated.View style={pulseStyle}>
      <TouchableOpacity
        style={[styles.button, { backgroundColor: isRecording ? '#FF3B30' : accentColor }]}
        onPress={handlePress}
        disabled={isTranscribing}
        activeOpacity={0.6}
        accessibilityLabel={
          isRecording ? 'Recording, tap to stop' :
          isTranscribing ? 'Transcribing audio' :
          'Start voice input'
        }
        accessibilityRole="button"
      >
        {isTranscribing ? (
          <ActivityIndicator size="small" color="#FFFFFF" />
        ) : (
          <Ionicons
            name={isRecording ? 'mic' : 'mic-outline'}
            size={14}
            color="#FFFFFF"
          />
        )}
      </TouchableOpacity>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  button: {
    width: BUTTON_SIZE,
    height: BUTTON_SIZE,
    borderRadius: BUTTON_SIZE / 2,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
});
